import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, useLocation, useHistory } from 'react-router-dom';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Page, LayoutSingleColumn, Footer } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import facebookImage from '../../assets/vivianlab-brandImage.png';
import twitterImage from '../../assets/vivianlab-brandImage.png';
import { withViewport } from '../../util/uiHelpers';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { useConfiguration } from '../../context/configurationContext';
import { getLocaleFromPathname } from '../../util/localeFromPathname';

import css from './TermsOfServicePage.module.css';
import ConsumerTermsEnglish from './Consumer/ConsumerTermsEnglish';
import ConsumerTermsGreek from './Consumer/ConsumerTermsGreek';
import ConsumerTermsGerman from './Consumer/ConsumerTermsGerman';
import ProfessionalTosEnglish from './Professional/ProfessionalTosEnglish';
import ProfessionalTosGreek from './Professional/ProfessionalTosGreek';
import ProfessionalTosGerman from './Professional/ProfessionalTosGerman';
import classNames from 'classnames';
import { isReactNativeWebView } from '../../util/isReactNativeWebView';

export const TermsOfServicePageComponent = props => {
  const { intl, scrollingDisabled } = props;
  const config = useConfiguration();
  const location = useLocation();
  const history = useHistory();
  const localeFromPath = getLocaleFromPathname(location?.pathname);
  const isGreek = localeFromPath === 'el';
  const isGerman = localeFromPath === 'de';

  // ── Query param handling ────────────────────────────────────────────────
  const query = new URLSearchParams(location.search);
  const tab = query.get('tab');

  // Default to consumer unless the professional tab is explicitly requested
  const isProfessional = tab === 'professional';

  const [scroll, setScroll] = useState(false);

  const siteTitle = config.marketplaceName;
  const schemaTitle = intl.formatMessage({ id: 'TermsOfServicePage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({
    id: 'TermsOfServicePage.schemaDescription',
  });

  const isNativeWebView = useMemo(
    () => isReactNativeWebView(location),
    [location?.search]
  );

  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;

  // ── Sticky header scroll handling ───────────────────────────────────────
  useEffect(() => {
    let isMounted = true;
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        if (isMounted) {
          setScroll(window.scrollY > 0);
        }
      };
      window.addEventListener('scroll', handleScroll);
      return () => {
        isMounted = false;
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);
  const isHeaderSticky = scroll ? css.sticky : '';

  // ── Content based on current tab + locale ───────────────────────────────
  const renderContent = isProfessional
    ? isGreek
      ? <ProfessionalTosGreek />
      : isGerman
        ? <ProfessionalTosGerman />
        : <ProfessionalTosEnglish />
    : isGreek
      ? <ConsumerTermsGreek />
      : isGerman
        ? <ConsumerTermsGerman />
        : <ConsumerTermsEnglish />;

  // ── Handlers to switch tabs (update URL only) ───────────────────────────
  const handleProfessionalClick = () => {
    history.push({
      pathname: location.pathname,
      search: '?tab=professional',
    });
  };

  const handleConsumerClick = () => {
    history.push({
      pathname: location.pathname,
      search: '?tab=consumer',
    });
  };

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={
          isNativeWebView ? null : <TopbarContainer isFullWidthHeader={true} isPostLandingPage />
        }
        footer={isNativeWebView ? null : <Footer />}
      >
        <div className={css.pageContent}>
          <div className={classNames(css.tabs, isHeaderSticky)}>
            <span
              className={classNames({
                [css.active]: isProfessional,
              })}
              onClick={handleProfessionalClick}
            >
              {intl.formatMessage({
                id: 'TermsOfServicePage.professionalUsers',
              })}
            </span>
            <span
              className={classNames({
                [css.active]: !isProfessional,
              })}
              onClick={handleConsumerClick}
            >
              {intl.formatMessage({
                id: 'TermsOfServicePage.consumers',
              })}
            </span>
          </div>
          {renderContent}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

TermsOfServicePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const TermsOfServicePage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(TermsOfServicePageComponent);

export default TermsOfServicePage;
